'use client';

import { useCallback, useRef } from 'react';

/**
 * Keeps one identity for the lifetime of the component while always invoking
 * the latest version of `callback`. Meant for handlers reached from memoized
 * structures — table column definitions, for instance — whose closures belong
 * to an earlier render. The ref is only read from the returned function, never
 * during render, so it cannot make the output of a render stale.
 */
export const useStableCallback = <TArgs extends unknown[], TResult>(
  callback: (...args: TArgs) => TResult,
) => {
  const latestCallback = useRef(callback);
  latestCallback.current = callback;

  return useCallback((...args: TArgs) => latestCallback.current(...args), []);
};
